import { useRef } from 'react';
import { Framework, IHistoryItem, useFramework } from '@/framework';
import { ILayerProps } from '@/version-types';

const STICKY_OPERATE_HISTORY_KEY = 'sticky_operate_history';

type IStickySwapOperateHistoryData = {
    delLayer: ILayerProps;
    addLayer: ILayerProps;
    info: {
        isExtraGroup: boolean;
        childIds: string[];
        childIndexes: number[];
        layerId: string;
        layerIndex: number;
        outFrameId: number;
        inFrameId: number;
    };
};

export class StickyOperateHistory extends Framework {
    constructor(identity: string) {
        super(identity);
        this.registerSwapHistory();
    }

    private registerSwapHistory() {
        this.framework.service.history.register(STICKY_OPERATE_HISTORY_KEY, {
            owner: 'plugin/mouse-operate/sticky',
            collaboration: 'local-only',
            onRedoUndo: (
                historyItem: IHistoryItem<IStickySwapOperateHistoryData>,
                isRedo: boolean,
                isUndo: boolean,
            ) => {
                const { info, delLayer, addLayer } = historyItem.data.data;
                const layer = isRedo ? addLayer : delLayer;
                this.framework.service.frame.restoreStickyInfo({
                    ...info,
                    layer,
                    isRedo,
                    isUndo,
                });
                this.framework.service.frame.setActiveLayer(layer.id);
            },
        });
    }
}

export const useStickyInfo = () => {
    const stickyInfo = useRef<{ in: number; out: number; layerId: string } | null>(null);
    const framework = useFramework();

    return {
        before() {
            stickyInfo.current = null;
        },
        pending(layerId: string) {
            stickyInfo.current = framework.service.frame.createSticky(layerId);
        },
        clear() {
            framework.service.frame.clearSticky();
        },
        after(delLayer: ILayerProps) {
            const stickyData = stickyInfo.current;
            if (!stickyData) return;
            const layerId = delLayer.id;
            const { frame: frameOperate } = framework.service;

            const info = frameOperate.swapToFrame(layerId, stickyData.in);
            if (!info) return;

            const addLayer = frameOperate.addOrUpdateLayer(
                framework.service.flatLayer.getLayer(layerId, true),
                info.inFrameId,
                true,
            );

            // 写入历史记录
            framework.service.history.write({
                type: STICKY_OPERATE_HISTORY_KEY,
                data: {
                    info,
                    delLayer,
                    addLayer,
                },
            });
        },
        hasSticky() {
            return !!stickyInfo.current;
        },
    };
};
